import assertExhaustive from '@bybeam/platform-lib/utilities/assertExhaustive';
import {
  DefaultIntroPage,
  IntroPage,
  StepsIntroPage,
  WelcomeIntroPage,
} from '@bybeam/platform-types';
import BulletList from '@platform-ui-common/components/BulletList/index.js';
import Button from '@platform-ui-common/components/Button/index.js';
import FormContainer from '@platform-ui-common/components/FormContainer/index.js';
import { IconType } from '@platform-ui-common/components/Icon/Icon.types.js';
import Markdown from '@platform-ui-common/components/Markdown/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import useProgram from '@platform-ui-common/hooks/contexts/useProgram.js';
import usePlaceholder from '@platform-ui-common/hooks/usePlaceholder.js';
import replacePlaceholders from '@platform-ui-common/utilities/placeholder.js';
import useApplicationConfig from '@platform-ui/application/hooks/useApplicationConfig.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import { IKImage } from 'imagekitio-react';
import { Redirect, useParams } from 'react-router-dom';

interface LayoutProps<PageType extends IntroPage = IntroPage> {
  page: PageType;
}

function ContinueButton({ page }: LayoutProps): JSX.Element {
  const { id: programId } = useProgram();
  const { introPages } = useApplicationConfig();
  const next = introPages[introPages.findIndex(({ key }) => key === page.key) + 1];

  return (
    <Button
      link={makeRoute(next ? ApplicantRoutes.INTRO_PAGE : ApplicantRoutes.SECTIONS_OVERVIEW, {
        programId: programId,
        screenKey: next?.key,
      })}
    >
      Continue
    </Button>
  );
}

function WelcomeLayout({ page }: LayoutProps<WelcomeIntroPage>): JSX.Element {
  const { name: programName, heroImage } = useProgram();
  const placeholders = usePlaceholder();

  return (
    <div>
      <div className="max-h-8 mb-6 flex w-full justify-center md:row-span-2 lg:justify-start">
        <IKImage
          src={heroImage}
          alt={`${programName} logo`}
          className="w-full h-auto object-contain"
          urlEndpoint="https://ik.imagekit.io/bybeam/"
          transformation={[
            { height: '400', width: '400', c: 'at_max', dpr: `${window.devicePixelRatio}` },
          ]}
        />
      </div>
      <FormContainer title={page.title} buttons={<ContinueButton page={page} />}>
        <div className="w-full flex flex-col gap-8 max-w-[36rem]">
          <Markdown variant="largeBody">{replacePlaceholders(page.body, placeholders)}</Markdown>
        </div>
      </FormContainer>
    </div>
  );
}

function StepsLayout({ page }: LayoutProps<StepsIntroPage>): JSX.Element {
  return (
    <FormContainer title={page.title} buttons={<ContinueButton page={page} />}>
      <div className="lg:max-w-[32rem] flex flex-col gap-8">
        {!!page?.copy && <Markdown variant="largeBody">{page.copy}</Markdown>}
        <BulletList
          list={page.steps.map(({ title, icon, ...step }) => ({
            key: title,
            title: <Typography variant="h3">{title}</Typography>,
            icon: icon as IconType,
            ...step,
          }))}
        />
      </div>
    </FormContainer>
  );
}

function DefaultLayout({ page }: LayoutProps<DefaultIntroPage>): JSX.Element {
  const placeholders = usePlaceholder();

  return (
    <FormContainer title={page.title} buttons={<ContinueButton page={page} />}>
      <div className="lg:max-w-[32rem] flex flex-col gap-8">
        <Markdown variant="largeBody">{replacePlaceholders(page.body, placeholders)}</Markdown>
      </div>
    </FormContainer>
  );
}

export default function IntroScreen(): JSX.Element {
  const { screenKey } = useParams<{ screenKey: string }>();
  const program = useProgram();
  const { introPages } = useApplicationConfig();

  const page = introPages.find(({ key }) => key === screenKey);
  if (!page)
    return <Redirect to={makeRoute(ApplicantRoutes.PROGRAM_INTRO, { programId: program.id })} />;

  switch (page.layout) {
    case 'welcome':
      return <WelcomeLayout page={page} />;
    case 'steps':
      return <StepsLayout page={page} />;
    case 'default':
      return <DefaultLayout page={page} />;
    default:
      return assertExhaustive(page);
  }
}
